import cx from "classnames";

import { useTheme } from "context/ThemeContext/ThemeContext";

/**
 * The design's three-way theme control: system / light / dark as icon-only
 * buttons in one glass pill. The highlight tracks the *chosen* mode, not the
 * resolved one, so "System" stays lit while following the OS.
 */
const MODES = [
  {
    mode: "system" as const,
    title: "System",
    icon: (
      <>
        <rect x="2" y="4" width="20" height="13" rx="2" />
        <path d="M8 21h8" />
        <path d="M12 17v4" />
      </>
    ),
  },
  {
    mode: "light" as const,
    title: "Light",
    icon: (
      <>
        <circle cx="12" cy="12" r="4.2" />
        <path d="M12 2v2.2M12 19.8V22M2 12h2.2M19.8 12H22M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M19.1 4.9l-1.6 1.6M6.5 17.5l-1.6 1.6" />
      </>
    ),
  },
  {
    mode: "dark" as const,
    title: "Dark",
    icon: <path d="M20.5 14.4A8.6 8.6 0 1 1 9.6 3.5a6.9 6.9 0 0 0 10.9 10.9Z" />,
  },
];

type Props = {
  /** `onGradient` styles the pill for the blue header; `plain` for panels. */
  surface?: "onGradient" | "plain";
  className?: string;
};

export function ThemeToggle({ surface = "onGradient", className }: Props) {
  const { themeMode, setThemeMode } = useTheme();
  const onGradient = surface === "onGradient";

  return (
    <div
      className={cx(
        "flex shrink-0 items-center gap-3 rounded-full p-3",
        onGradient
          ? "border border-white/35 bg-white/20 backdrop-blur-[2px] backdrop-saturate-[3]"
          : "border border-slate-600 bg-slate-800",
        className
      )}
      role="group"
      aria-label="Color theme"
    >
      {MODES.map((item) => {
        const isActive = themeMode === item.mode;

        return (
          <button
            key={item.mode}
            type="button"
            title={item.title}
            aria-label={item.title}
            aria-pressed={isActive}
            onClick={() => setThemeMode(item.mode)}
            className={cx(
              "grid h-30 w-30 place-items-center rounded-full transition-all",
              isActive
                ? onGradient
                  ? "bg-white text-[#236f92]"
                  : "bg-blue-500 text-white"
                : onGradient
                  ? "bg-transparent text-white/80 hover:text-white"
                  : "bg-transparent text-typography-secondary hover:text-typography-primary"
            )}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {item.icon}
            </svg>
          </button>
        );
      })}
    </div>
  );
}
